import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { apiClient } from "../api/apiClient";
import { getToken } from "../Helper/auth";
import {
  ArrowLeft, Server, Clock, CheckCircle,
  Loader, PlayCircle, CreditCard,
} from "lucide-react";
import { motion } from "framer-motion";

export default function MigrationJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executingJob, setExecutingJob] = useState(null);
  const navigate = useNavigate();

  const fetchJobs = async () => {
    try {
      const { data } = await supabase.auth.getUser();
      const userId = data?.user?.id;
      if (!userId) return;
      const res = await apiClient.get(`/user-jobs/${userId}`);
      setJobs(res.jobs || []);
    } catch (err) {
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, []);

  const handlePayment = async (job) => {
    const jobId = job.id || job.job_id;
    if (!jobId) { alert("Job ID not found"); return; }
    if (job.payment_status === "completed") { alert("Payment already completed for this job"); return; }

    setExecutingJob(`pay-${jobId}`);
    try {
      const token = await getToken();
      if (!token) { alert("Session expired. Please login again."); navigate("/login"); return; }

      const payRes = await apiClient.post(
        "/create-payment-intent",
        { job_id: jobId, amount: job.expected_amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (payRes.checkout_url) {
        window.location.href = payRes.checkout_url;
      } else {
        alert("Failed to create payment session");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || "Failed to process payment";
      alert(errorMsg);
    } finally {
      setExecutingJob(null);
    }
  };

  const handleExecuteMigration = (job) => {
    const jobId = job.id || job.job_id;
    if (!jobId) { alert("Job ID not found"); return; }
    if (job.payment_status !== "completed") { alert("Please complete payment before executing migration"); return; }
    navigate(`/payment-success/${jobId}`);
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-text-primary">
        <Loader className="animate-spin text-text-muted" size={32} />
        <span className="ml-3 text-text-secondary">Loading jobs...</span>
      </div>
    );

  return (
    <div className="min-h-screen bg-bg text-text-primary px-3 sm:px-6 py-8">

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 mb-6 text-text-muted hover:text-text-primary transition"
      >
        <ArrowLeft size={18} /> Back
      </button>

      <h1 className="text-2xl sm:text-3xl font-semibold mb-8 text-text-primary text-center">
        Your Migration Jobs
      </h1>

      {jobs.length === 0 ? (
        <p className="text-text-muted text-center">No migration jobs found.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-7xl mx-auto">
          {jobs.map((job, index) => {
            const jobId = job.id || job.job_id;
            const isCompleted = job.job_status === "completed";
            const job_status = isCompleted ? "COMPLETED" : `${job.job_status}`.toUpperCase();
            const isPaymentCompleted = job.payment_status === "completed";
            const payment_status = isPaymentCompleted ? "PAID" : job.payment_status === "pending" ? "PENDING" : "FREE";

            return (
              <motion.div
                key={jobId || index}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`relative bg-white border rounded-xl p-5 shadow-sm transition-colors
                  ${isCompleted && isPaymentCompleted
                    ? "border-green-400"
                    : "border-border hover:border-border-strong"
                  }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-text-primary">
                    <Server size={18} className="text-text-muted" />
                    <span className="text-md font-medium">Job {index + 1}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium
                      ${isCompleted
                        ? "bg-green-50 border-green-400 text-green-700"
                        : "bg-bg-soft border-border text-text-muted"
                      }`}
                    >
                      {isCompleted ? <CheckCircle size={12} /> : <Loader size={12} />}
                      {job_status}
                    </span>

                    <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium
                      ${isPaymentCompleted
                        ? "bg-green-50 border-green-400 text-green-700"
                        : "bg-yellow-50 border-yellow-400 text-yellow-700"
                      }`}
                    >
                      {isPaymentCompleted ? <CheckCircle size={12} /> : <CreditCard size={12} />}
                      {payment_status}
                    </span>
                  </div>
                </div>

                {/* Job ID */}
                <div className="mb-4">
                  <p className="text-text-muted text-xs">Job ID</p>
                  <p className="text-text-primary font-medium text-xs break-all">{jobId}</p>
                </div>

                {/* Details */}
                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                  {[
                    { label: "Migration Type", value: job.migration_type },
                    { label: "Progress",       value: `${job.progress}%` },
                    { label: "Vectors",        value: job.vector_count   },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-text-muted text-xs">{label}</p>
                      <p className="text-text-primary font-medium">{value}</p>
                    </div>
                  ))}
                </div>

                {/* Source & Destination */}
                <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                  <div>
                    <p className="text-text-muted text-xs">Source</p>
                    <p className="text-text-primary font-medium">{job.source_info.source_collection_name}</p>
                  </div>
                  <div>
                    <p className="text-text-muted text-xs">Destination</p>
                    <p className="text-text-primary font-medium">{job.target_info.target_collection_name}</p>
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <p className="text-text-muted text-xs">Amount</p>
                  <p className="text-yellow-600 font-semibold">$ {job.expected_amount}</p>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full bg-bg-soft border border-border h-2 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${job.progress}%` }}
                      transition={{ duration: 0.6 }}
                      className={`h-full ${isCompleted ? "bg-green-500" : "bg-accent"}`}
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    <Clock size={12} />
                    {new Date(job.created_at).toLocaleString()}
                  </div>

                  {!isCompleted && (
                    <div className="flex items-center gap-2">
                      {!isPaymentCompleted && (
                        <button
                          onClick={() => handlePayment(job)}
                          disabled={executingJob === `pay-${jobId}`}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition
                            ${executingJob === `pay-${jobId}`
                              ? "bg-bg-soft text-text-muted border border-border cursor-not-allowed"
                              : "bg-yellow-400 hover:bg-yellow-500 text-yellow-900"
                            }`}
                        >
                          {executingJob === `pay-${jobId}` ? (
                            <><Loader size={14} className="animate-spin" /><span>Processing...</span></>
                          ) : (
                            <><CreditCard size={14} /><span>Pay Now</span></>
                          )}
                        </button>
                      )}

                      {isPaymentCompleted && (
                        <button
                          onClick={() => handleExecuteMigration(job)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition bg-accent hover:bg-accent-soft text-white"
                        >
                          <PlayCircle size={14} />
                          <span>{job.job_status === "processing" ? "Retry" : "Execute"}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}